<template>
  <div id="pageItemList">
    <page-header :title="$t('detailedReport')"></page-header>
    <v-container grid-list-xl fluid>
      <v-layout row wrap>
        <v-flex lg12>
          <v-card>
            <v-card-text>
              <v-form ref="editCreateForm">
                <v-container grid-list-md fluid>
                  <v-layout wrap justify-space-between>
                    <v-flex xs8>
                      <v-autocomplete
                        name="vehicles"
                        :label="$t('vehicle')"
                        :loading="vehicleIsLoading"
                        :items="vehicles"
                        :search-input.sync="vehicleSearch"
                        v-model="reportParams.vehicle"
                        cache-items
                        clearable
                        prepend-icon="train"
                        item-text="name"
                        item-value="id"
                        v-validate="'required'"
                        :data-vv-as="$t('vehicle')"
                        :error-messages="errors.collect('vehicles')"
                      ></v-autocomplete>
                    </v-flex>
                    <v-flex xs2>
                      <v-menu
                        :close-on-content-click="false"
                        v-model="date.fromMenu"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        min-width="290px"
                      >
                        <v-text-field
                          name="date_from"
                          slot="activator"
                          :value="
                            reportParams.dateFrom
                              ? $moment(reportParams.dateFrom).format('LL')
                              : null
                          "
                          :label="$t('dateFrom')"
                          v-validate="'required'"
                          :data-vv-as="$t('dateFrom')"
                          :error-messages="errors.collect('date_from')"
                          readonly
                        ></v-text-field>
                        <v-date-picker
                          v-model="reportParams.dateFrom"
                          @input="date.fromMenu = false"
                          :locale="LANG"
                          reactive
                          :max="$moment().format('YYYY-MM-DD')"
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>
                    <v-flex xs2>
                      <v-menu
                        :close-on-content-click="false"
                        v-model="date.untilMenu"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        min-width="290px"
                      >
                        <v-text-field
                          name="date_until"
                          slot="activator"
                          :value="
                            reportParams.dateUntil
                              ? $moment(reportParams.dateUntil).format('LL')
                              : null
                          "
                          :label="$t('dateUntil')"
                          v-validate="'required'"
                          :data-vv-as="$t('dateUntil')"
                          :error-messages="errors.collect('date_until')"
                          readonly
                        ></v-text-field>
                        <v-date-picker
                          v-model="reportParams.dateUntil"
                          @input="date.untilMenu = false"
                          :locale="LANG"
                          reactive
                          :max="$moment().format('YYYY-MM-DD')"
                        ></v-date-picker>
                      </v-menu>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                :href="getDownloadUrl"
                :disabled="!formIsReady"
                target="_blank"
                color="success"
              >
                {{ $t("download") }}
                <v-icon right>save_alt</v-icon>
              </v-btn>
              <v-btn @click="generate()" color="blue-grey" class="white--text">
                {{ $t("generate") }}
                <v-icon right>receipt</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
        <v-flex lg12>
          <v-card>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="getItems()"
                :loading="reportIsLoading"
                class="elevation-1"
                hide-actions
              >
                <template slot="items" slot-scope="props">
                  <td>
                    <au-vehicle :vehicle="props.item.vehicle" />
                  </td>
                  <td>{{ props.item.date_time_from | moment("LLL") }}</td>
                  <td>{{ props.item.date_time_until | moment("LLL") }}</td>
                  <td>{{ props.item.distance }} km</td>
                  <td>{{ props.item.issue_count }}</td>
                  <td>{{ props.item.critical_issue_count }}</td>
                  <td>{{ props.item.automatic_issues_count }}</td>
                  <td>
                    {{ props.item.vehicle_inaccessible_in_hours }} godzin
                  </td>
                  <td>{{ props.item.vehicle_readiness_coefficient | percent }}</td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex lg12>
          <v-card>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="headersIssues"
                :items="report.issues"
                :loading="reportIsLoading"
                class="elevation-1"
                hide-actions
              >
                <template slot="items" slot-scope="props">
                  <td>
                    {{
                      lodash
                        .uniq(
                          props.item.service_reports.map(sr => {
                            return sr.product_group.name;
                          })
                        )
                        .join(", ") | lackOfData
                    }}
                  </td>
                  <td>
                    {{
                      lodash
                        .uniq(
                          props.item.service_reports.map(sr => {
                            return sr.component.name;
                          })
                        )
                        .join(", ") | lackOfData
                    }}
                  </td>
                  <td>
                    {{
                      lodash
                        .uniq(
                          props.item.service_reports.map(sr => {
                            return sr.vehicle_part;
                          })
                        )
                        .join(", ") | lackOfData
                    }}
                  </td>
                  <td>
                    {{
                      lodash
                        .uniq(
                          props.item.service_reports.map(sr => {
                            return $t("issueCategories." + sr.issue_category);
                          })
                        )
                        .join(", ") | lackOfData
                    }}
                  </td>
                  <td>
                    {{
                      lodash
                        .uniq(
                          props.item.service_reports.map(sr => {
                            return sr.number_of_occurrences;
                          })
                        )
                        .join(", ") | lackOfData
                    }}
                  </td>
                  <td>
                    {{ props.item.duration_in_minutes | detailedDuration }}
                  </td>
                  <td>{{ props.item.resolved | booleanText }}</td>
                  <td>
                    <v-btn
                      :to="{
                        name: 'IssueShow',
                        params: { id: props.item.id }
                      }"
                      flat
                      icon
                      color="primary"
                    >
                      <v-icon>details</v-icon>
                    </v-btn>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";
import { FETCH_DETAILED_REPORT, FETCH_VEHICLES } from "@/store/actions.type";
import JwtService from "../../common/jwt.service";
import store from "@/store";
import { RESET_REPORT } from "../../store/actions.type";

export default {
  data() {
    return {
      lodash: _,
      date: {
        fromMenu: false,
        untilMenu: false
      },
      vehicleSearch: "",
      reportParams: {
        vehicle: [],
        dateFrom: null,
        dateUntil: null
      },
      LANG: process.env.VUE_APP_LANG,
      headers: [
        { text: this.$t("vehicle"), sortable: false },
        { text: this.$t("dataFrom"), sortable: false },
        { text: this.$t("dataUntil"), sortable: false },
        { text: this.$t("distance"), sortable: false },
        { text: this.$t("issuesCount"), sortable: false },
        { text: this.$t("criticalIssuesCount"), sortable: false },
        { text: this.$t("automaticIssuesCount"), sortable: false },
        { text: this.$t("vehicleInaccessibility"), sortable: false },
        { text: this.$t("vehicleReadiness"), sortable: false }
      ],
      headersIssues: [
        { text: this.$t("productGroups"), sortable: false },
        { text: this.$t("component"), sortable: false },
        { text: this.$t("vehiclePart"), sortable: false },
        { text: this.$t("issueCategory"), sortable: false },
        { text: this.$t("numberOfOccurrences"), sortable: false },
        { text: this.$t("duration"), sortable: false },
        { text: this.$t("resolved"), sortable: false },
        { text: this.$t("actions"), sortable: false }
      ]
    };
  },
  computed: {
    ...mapGetters([
      "report",
      "reportIsLoading",
      "vehicles",
      "vehicleIsLoading"
    ]),
    getDownloadUrl() {
      return (
        process.env.VUE_APP_API_URL +
        "/report/detailed/file?dateFrom=" +
        this.reportParams.dateFrom +
        "&dateUntil=" +
        this.reportParams.dateUntil +
        "&vehicle=" +
        this.reportParams.vehicle +
        "&bearer=" +
        JwtService.getToken()
      );
    },
    formIsReady() {
      return (
        this.reportParams.vehicle &&
        this.reportParams.dateFrom &&
        this.reportParams.dateUntil
      );
    }
  },
  watch: {
    vehicleSearch: _.debounce(function(val) {
      this.$store.dispatch(FETCH_VEHICLES, [{ rowsPerPage: -1 }, val]);
    }, 300)
  },
  methods: {
    generate() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$store.dispatch(FETCH_DETAILED_REPORT, this.reportParams);
        }
      });
    },
    getItems() {
      return !_.isEmpty(this.report) ? [this.report] : [];
    }
  },
  beforeRouteEnter(to, from, next) {
    Promise.all([store.dispatch(RESET_REPORT)]).then(() => {
      next();
    });
  }
};
</script>

<style scoped></style>
